import { ApiError } from './api'
import i18n from '../i18n'

// Map the handful of recurring raw English DRF phrases to localized text so
// error toasts/inline messages aren't English on Arabic pages (Bug 7 / 24).
export function localizeDrfMessage(msg: string): string {
  const m = msg.toLowerCase()
  // Licence denial, not permission denial. Must be tested BEFORE the generic
  // 'invalid' branch below, which would otherwise swallow it. Mirrors
  // AI_NOT_LICENSED_DETAIL in django/apps/praxi_apps/ai/permissions.py — the two
  // strings are a matched pair; change one and you must change the other.
  if (m.includes('not licensed'))
    return i18n.t('common.aiNotLicensed', 'AI is not licensed for this clinic.')
  if (m.includes('may not be blank') || m.includes('this field is required') || m.includes('required'))
    return i18n.t('common.fieldRequired', 'This field is required')
  if (m.includes('not a valid choice'))
    return i18n.t('common.invalidChoice', 'Invalid selection')
  if (m.includes('already exists') || m.includes('must be unique') || m.includes('must make a unique set'))
    return i18n.t('common.alreadyExists', 'Already exists')
  if (m.includes('not a valid') || m.includes('invalid'))
    return i18n.t('common.invalidValue', 'Invalid value')
  return msg
}

function humanizeField(key: string): string {
  const fallback = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return i18n.t(`fields.${key}`, fallback)
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

/**
 * Extract a user-friendly error message from an API error.
 * Handles ApiError (from lib/api.ts) and generic Error objects.
 */
export function extractErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  if (error instanceof ApiError) {
    // Status-transition rejections carry {detail, from, to}. Without this branch the
    // detail ("Invalid status transition.") falls through to localizeDrfMessage's
    // generic 'invalid' arm and is flattened to "Invalid value" — which is how R-9
    // presented: a precise, actionable backend error shown as a generic toast.
    //
    // Keyed on the {from, to} PAYLOAD SHAPE rather than on the message text, so the
    // shared generic branch is left untouched and no other caller changes behaviour.
    const d = error.data as { from?: unknown; to?: unknown } | undefined
    if (d && typeof d === 'object' && typeof d.from === 'string' && typeof d.to === 'string') {
      return i18n.t('common.invalidTransition', {
        from: d.from, to: d.to,
        defaultValue: 'Invalid status transition: {{from}} → {{to}}',
      })
    }
    if (error.nonFieldError) return localizeDrfMessage(error.nonFieldError)
    if (error.fieldErrors && Object.keys(error.fieldErrors).length > 0) {
      return Object.entries(error.fieldErrors)
        .map(([k, msgs]) => `${humanizeField(k)}: ${msgs.map(localizeDrfMessage).join(', ')}`)
        .join(' · ')
    }
    return error.message
  }

  if (error instanceof Error) {
    if (error.message === 'Failed to fetch') return i18n.t('common.networkError', 'Network error. Please try again.')
    return error.message || fallback
  }

  return fallback
}
